<template>
  <DialogComponent
    ref="filterDialog"
    type="bottomsheet"
    v-slot="{ dialogVisible }"
    @closed="onClosed"
  >
    <div class="pb-8 max-h-[90vh] overflow-y-auto">
      <!-- Handle -->
      <div class="flex justify-center mb-6">
        <div class="w-12 h-1.5 bg-gray-300 rounded-full"></div>
      </div>

      <!-- Header -->
      <div class="flex items-center justify-between mb-6 px-2">
        <h2 class="text-xl font-bold text-gray-900">Filtres</h2>
        <div
          v-if="activeFiltersCount > 0"
          class="px-3 py-1 bg-fuchsia-100 text-fuchsia-800 rounded-full text-sm font-medium cursor-pointer hover:bg-fuchsia-200 transition-colors"
          @click="resetFilters"
        >
          Réinitialiser ({{ activeFiltersCount }})
        </div>
      </div>

      <!-- Price Range -->
      <div class="mb-8 px-2">
        <h3 class="font-semibold text-gray-900 mb-4">Prix</h3>
        <div class="flex items-center gap-3">
          <div class="flex-1">
            <label class="block text-xs text-gray-500 mb-1">Min (FCFA)</label>
            <input
              v-model.number="localFilters.minPrice"
              type="number"
              placeholder="Min"
              class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-fuchsia-600 focus:border-fuchsia-600 outline-none text-center"
            />
          </div>
          <span class="text-gray-400 pt-6">-</span>
          <div class="flex-1">
            <label class="block text-xs text-gray-500 mb-1">Max (FCFA)</label>
            <input
              v-model.number="localFilters.maxPrice"
              type="number"
              placeholder="Max"
              class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-fuchsia-600 focus:border-fuchsia-600 outline-none text-center"
            />
          </div>
        </div>
      </div>

      <!-- Date Range -->
      <div class="mb-8 px-2">
        <h3 class="font-semibold text-gray-900 mb-4">Dates</h3>
        <div class="space-y-3">
          <!-- Date Display/Trigger -->
          <div
            @click="showDatePicker = !showDatePicker"
            class="w-full px-4 py-3 border-2 rounded-xl cursor-pointer transition-all duration-200 flex items-center justify-between"
            :class="hasDateRange
              ? 'border-fuchsia-700 bg-fuchsia-50 text-fuchsia-800'
              : 'border-gray-200 text-gray-700 hover:border-gray-300'"
          >
            <span class="text-sm">
              {{ dateRangeLabel }}
            </span>
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>

          <!-- Date Picker (Collapsible) -->
          <div v-if="showDatePicker" class="mt-3 p-4 border border-gray-200 rounded-xl bg-white">
            <DatePickerInline
              v-model="localFilters.dateRange"
              :isRange="true"
              :multiCalendars="true"
              :minDate="new Date()"
              placeholder="Sélectionnez une période"
            />
            <div v-if="localFilters.dateRange && localFilters.dateRange.length === 2" class="flex gap-2 mt-3">
              <button
                @click="localFilters.dateRange = []"
                class="flex-1 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Effacer
              </button>
              <button
                @click="showDatePicker = false"
                class="flex-1 py-2 text-sm text-white bg-fuchsia-700 rounded-lg hover:bg-fuchsia-800"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Categories -->
      <div class="mb-8 px-2">
        <h3 class="font-semibold text-gray-900 mb-4">Catégories</h3>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="category in categories"
            :key="category.id"
            @click="toggleCategory(category.id)"
            class="px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border-2 flex items-center gap-2"
            :class="localFilters.categories.includes(category.id)
              ? 'border-fuchsia-700 bg-fuchsia-50 text-fuchsia-800'
              : 'border-gray-200 text-gray-700 hover:border-gray-300'"
          >
            <span>{{ category.icon }}</span>
            <span>{{ category.label }}</span>
          </button>
        </div>
      </div>

      <!-- Country -->
      <div class="mb-8 px-2">
        <h3 class="font-semibold text-gray-900 mb-4">Pays</h3>
        <div class="relative">
          <select
            v-model="localFilters.country"
            class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-fuchsia-600 focus:border-fuchsia-600 outline-none appearance-none bg-white text-gray-700"
            :class="localFilters.country ? 'border-fuchsia-700 bg-fuchsia-50 text-fuchsia-800' : ''"
          >
            <option value="">Tous les pays</option>
            <option v-for="country in countries" :key="country.code" :value="country.code">
              {{ country.name }}
            </option>
          </select>
          <svg class="w-5 h-5 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      <!-- Apply Button -->
      <div class="px-2 mt-8">
        <button
          @click="applyFilters"
          class="w-full py-4 bg-gradient-to-r from-fuchsia-800 to-fuchsia-900 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl hover:from-fuchsia-900 hover:to-purple-900 transition-all duration-200"
        >
          Voir les résultats
          <span v-if="resultsCount > 0" class="ml-2">({{ resultsCount }})</span>
        </button>
      </div>
    </div>
  </DialogComponent>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';
import DatePickerInline from '@ui/core/input/DatePickerInline.global.vue';

const props = defineProps({
  filters: {
    type: Object,
    default: () => ({})
  },
  categories: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['apply', 'closed']);

const filterDialog = ref(null);
const showDatePicker = ref(false);

// West African countries
const countries = [
  { code: 'BJ', name: 'Bénin' },
  { code: 'TG', name: 'Togo' },
  { code: 'BF', name: 'Burkina Faso' },
  { code: 'CI', name: 'Côte d\'Ivoire' },
  { code: 'SN', name: 'Sénégal' },
  { code: 'ML', name: 'Mali' }
];

// Local filters state (copie des props)
const localFilters = ref({
  minPrice: null,
  maxPrice: null,
  dateRange: [],
  categories: [],
  country: ''
});

// Watch for external filter changes
watch(() => props.filters, (newFilters) => {
  if (newFilters) {
    localFilters.value = { ...newFilters };
  }
}, { immediate: true, deep: true });

const activeFiltersCount = computed(() => {
  let count = 0;
  if (localFilters.value.minPrice) count++;
  if (localFilters.value.maxPrice) count++;
  if (localFilters.value.dateRange && localFilters.value.dateRange.length === 2) count++;
  if (localFilters.value.categories.length > 0) count++;
  if (localFilters.value.country) count++;
  return count;
});

const hasDateRange = computed(() => {
  return localFilters.value.dateRange && localFilters.value.dateRange.length === 2;
});

const dateRangeLabel = computed(() => {
  if (!localFilters.value.dateRange || localFilters.value.dateRange.length !== 2) {
    return 'Sélectionnez des dates';
  }
  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };
  return `${formatDate(localFilters.value.dateRange[0])} - ${formatDate(localFilters.value.dateRange[1])}`;
});

const resultsCount = computed(() => {
  // This would be calculated based on actual filtered results
  return 0;
});

const toggleCategory = (categoryId) => {
  const index = localFilters.value.categories.indexOf(categoryId);
  if (index > -1) {
    localFilters.value.categories.splice(index, 1);
  } else {
    localFilters.value.categories.push(categoryId);
  }
};

const resetFilters = () => {
  localFilters.value = {
    minPrice: null,
    maxPrice: null,
    dateRange: [],
    categories: [],
    country: ''
  };
  showDatePicker.value = false;
};

const applyFilters = () => {
  emit('apply', { ...localFilters.value });
  filterDialog.value?.hide();
};

const onClosed = () => {
  emit('closed');
};

const show = () => {
  filterDialog.value?.show();
};

const hide = () => {
  filterDialog.value?.hide();
};

defineExpose({ show, hide });
</script>
